import {
  ChangeDetectionStrategy, Component,
  ElementRef,
  HostListener,
  OnDestroy,
  OnInit,
  ViewChild,
  signal,
  inject,
  PLATFORM_ID
} from '@angular/core';
import { isPlatformBrowser } from '@angular/common';

const excludedMessages = [
  'WAUB02005',
  'WAUB02006',
  'WAUB02007',
  'WAUB02002',
  'WAUB02003',
  'WAUB02004'
];

export type LoginPage = 'SELECT_SERVICE' | 'ENTER_IDENTIFIER' | 'VERIFY' | 'SUCCESS' | 'CONTINUE_EXPLORE' | 'VIEW_ACCOUNT' | '';

@Component({
  selector: 'app-login-iframe',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './login-iframe.component.html',
  styleUrl: './login-iframe.component.scss'
})
export class LoginIframeComponent implements OnInit, OnDestroy {
  @ViewChild('loginOverlay') overlayRef!: ElementRef<HTMLDivElement>;
  @ViewChild('loginContainer') containerRef!: ElementRef<HTMLDivElement>;
  @ViewChild('loginIframe') iframeRef!: ElementRef<HTMLIFrameElement>;

  private platformId = inject(PLATFORM_ID);

  // State
  isOpen = signal(false);
  isLoading = signal(false);
  hasReceivedResize = signal(false);
  currentPage = signal<LoginPage>('');

  // Snackbar
  showSnackbar = signal(false);
  private snackbarTimeoutId: ReturnType<typeof setTimeout> | null = null;

  // Timeouts
  private resizeTimeoutId: ReturnType<typeof setTimeout> | null = null;
  private debounceResizeTimeout: ReturnType<typeof setTimeout> | null = null;

  // Constants
  private readonly TIMEOUT_MS = 10000;
  private readonly IGNORE_AFTER_FIRST_MS = 100;

  // Page max widths for desktop
  private readonly pageMaxWidths: Record<string, string> = {
    'SELECT_SERVICE': '640px',
    'ENTER_IDENTIFIER': '686px',
    'VERIFY': '640px',
    'SUCCESS': '640px'
  };

  get isMobile(): boolean {
    if (!isPlatformBrowser(this.platformId)) return false;
    return window.innerWidth < 768;
  }

  ngOnInit(): void {
    if (isPlatformBrowser(this.platformId)) {
      document.addEventListener('login-service:open', this.handleOpenEvent);
    }
  }

  private handleOpenEvent = (): void => {
    this.open();
  };

  get iframeUrl(): string {
    if (!isPlatformBrowser(this.platformId)) return '';
    return globalThis.location.origin + '/login/select-service';
  }

  ngOnDestroy(): void {
    this.clearResizeTimeout();
    this.clearDebounceResizeTimeout();
    this.clearSnackbarTimeout();
    if (isPlatformBrowser(this.platformId)) {
      document.removeEventListener('login-service:open', this.handleOpenEvent);
    }
  }

  // ========== Event Handlers ==========

  @HostListener('window:message', ['$event'])
  handleIframeMessage(event: MessageEvent): void {
    if (!this.isOpen()) return;

    const data = event.data;

    if (data?.type === 'resize') {
      const height = data.height;
      if (height) {
        if (this.debounceResizeTimeout) {
          return;
        }
        this.applyResize(height);
        this.debounceResizeTimeout = setTimeout(() => {
          this.debounceResizeTimeout = null;
        }, this.IGNORE_AFTER_FIRST_MS);
      }
    } else if (data?.type === 'page') {
      const page = data.page as LoginPage;
      this.handlePage(page);

      if (page === 'SUCCESS') {
        document.dispatchEvent(new CustomEvent('login-service:success'));
      } else if (page === 'CONTINUE_EXPLORE') {
        this.close();
      } else if (page === 'VIEW_ACCOUNT') {
        this.close();
        setTimeout(() => {
          if (isPlatformBrowser(this.platformId)) {
            window.location.href = window.location.origin + '/user';
          }
        }, 300);
      }
    } else if (data?.type === 'error' && data.message !== 'UNEXPECTED_ERROR') {
      if (!excludedMessages.includes(data.message)) {
        this.showSnackbarSignal();
      }
    }
  }

  @HostListener('document:keydown', ['$event'])
  handleKeydown(event: KeyboardEvent): void {
    if (event.key === 'Escape' && this.isOpen()) {
      this.close();
    }
  }

  handleOverlayClick(): void {
    this.close();
  }

  handleCloseClick(): void {
    this.close();
  }

  onIframeLoad(): void {
    if (this.hasReceivedResize()) {
      this.isLoading.set(false);
    }
  }

  // ========== Page Handler ==========

  private handlePage(page: LoginPage): void {
    this.currentPage.set(page);

    if (this.isMobile) return;

    const maxWidth = this.pageMaxWidths[page];
    if (maxWidth) {
      if (this.containerRef?.nativeElement) {
        this.containerRef.nativeElement.style.maxWidth = maxWidth;
      }
      if (isPlatformBrowser(this.platformId)) {
        localStorage.setItem('login_popup_width', maxWidth);
      }
    }
  }

  // =========─ Resize Handlers ==========

  private applyResize(height: number): void {
    if (!this.iframeRef?.nativeElement || !this.containerRef?.nativeElement) return;

    this.iframeRef.nativeElement.style.height = `${height}px`;
    this.containerRef.nativeElement.style.height = `${height}px`;

    if (this.isMobile) {
      this.containerRef.nativeElement.style.width = '100%';
    } else {
      this.containerRef.nativeElement.style.width = '100%';
      const savedWidth = isPlatformBrowser(this.platformId)
        ? localStorage.getItem('login_popup_width')
        : null;
      if (savedWidth) {
        this.containerRef.nativeElement.style.maxWidth = savedWidth;
      }
    }

    this.iframeRef.nativeElement.hidden = false;
    this.hasReceivedResize.set(true);
    this.isLoading.set(false);
    this.isOpen.set(true);
  }

  private showDefaultDimensions(): void {
    const height = 480;
    if (this.isMobile) {
      this.applyResize(height);
    } else {
      this.applyResize(360);
    }
    this.isLoading.set(false);
  }

  // ========== Open/Close ==========

  open(): void {
    if (this.isOpen()) return;

    this.isOpen.set(true);
    this.isLoading.set(true);
    this.hasReceivedResize.set(false);
    this.currentPage.set('');

    if (this.iframeRef?.nativeElement) {
      this.iframeRef.nativeElement.hidden = true;
      this.iframeRef.nativeElement.style.height = '';
      this.iframeRef.nativeElement.style.width = '';
      this.iframeRef.nativeElement.src = this.iframeUrl;
    }

    if (this.containerRef?.nativeElement) {
      this.containerRef.nativeElement.style.height = '';
      this.containerRef.nativeElement.style.width = '';

      if (!this.isMobile) {
        const savedWidth = isPlatformBrowser(this.platformId)
          ? localStorage.getItem('login_popup_width')
          : null;
        if (savedWidth) {
          this.containerRef.nativeElement.style.maxWidth = savedWidth;
        }
      }
    }

    if (isPlatformBrowser(this.platformId)) {
      document.body.classList.add('login-open');
      document.body.style.overflow = 'hidden';
    }

    this.resizeTimeoutId = setTimeout(() => {
      if (this.isOpen() && !this.hasReceivedResize()) {
        this.showDefaultDimensions();
      }
    }, this.TIMEOUT_MS);
  }

  close(): void {
    this.isOpen.set(false);
    this.hasReceivedResize.set(false);
    this.currentPage.set('');
    this.clearResizeTimeout();
    this.clearDebounceResizeTimeout();

    if (isPlatformBrowser(this.platformId)) {
      document.body.classList.remove('login-open');
      document.body.style.overflow = '';
      document.body.style.position = '';
      document.body.style.top = '';
    }

    if (this.iframeRef?.nativeElement) {
      this.iframeRef.nativeElement.src = '';
      this.iframeRef.nativeElement.hidden = true;
    }

    document.dispatchEvent(new CustomEvent('login-service:closed'));
  }

  // ========== Snackbar ==========

  showSnackbarSignal(): void {
    this.clearSnackbarTimeout();
    this.showSnackbar.set(true);
    this.snackbarTimeoutId = setTimeout(() => {
      this.hideSnackbar();
    }, 3000);
  }

  hideSnackbar(): void {
    this.showSnackbar.set(false);
  }

  // =========─ Cleanup ==========

  private clearResizeTimeout(): void {
    if (this.resizeTimeoutId) {
      clearTimeout(this.resizeTimeoutId);
      this.resizeTimeoutId = null;
    }
  }

  private clearDebounceResizeTimeout(): void {
    if (this.debounceResizeTimeout) {
      clearTimeout(this.debounceResizeTimeout);
      this.debounceResizeTimeout = null;
    }
  }

  private clearSnackbarTimeout(): void {
    if (this.snackbarTimeoutId) {
      clearTimeout(this.snackbarTimeoutId);
      this.snackbarTimeoutId = null;
    }
  }
}
